/**
 * UsageStatsCitationsSection Component - Citations/references section
 *
 * Extracted from UsageStats.tsx
 */

import React from 'react';
import type { Theme } from '../../themes/theme';
import type { CitationData } from './types/stats.types';
import { formatPeriod } from '../../utils/formatPeriod';

interface UsageStatsCitationsSectionProps {
  citations?: Record<string, CitationData>;
  onCitationClick?: (citation: CitationData) => void;
  theme: Theme;
}

export const UsageStatsCitationsSection: React.FC<UsageStatsCitationsSectionProps> = ({
  citations,
  onCitationClick,
  theme,
}) => {
  if (!citations || Object.keys(citations).length === 0) return null;

  const getCitationLabel = (citation: CitationData) => {
    const parts: string[] = [];

    // Tipo de documento (ej: "Análisis", "FECU")
    if (citation.document_type) {
      const docType =
        citation.document_type === 'ANALISIS'
          ? 'Análisis'
          : citation.document_type === 'FECU'
            ? 'FECU'
            : citation.document_type;
      parts.push(docType);
    }

    // Período si existe (ej: "1T 2025", "oct2025")
    if (citation.document_period) {
      parts.push(formatPeriod(citation.document_period, citation.document_type));
    }

    // Si no hay tipo ni período, usar nombre del documento (truncado)
    if (parts.length === 0) {
      const name = citation.document_name || 'Documento';
      const shortName =
        name
          .replace(/\.[^/.]+$/, '')
          .split('/')
          .pop() || name;
      return shortName.length > 30 ? shortName.substring(0, 27) + '...' : shortName;
    }

    return parts.join(' ');
  };

  const uniqueDocsCount = new Set(
    Object.values(citations).map((c) => c.document_type || c.document_name)
  ).size;

  return (
    <div className={`mt-1 pt-1 border-t ${theme.colors.border.primary}/30 space-y-1`}>
      {/* Header Referencias */}
      <div className="flex items-center justify-between">
        <div className="text-[10px] flex items-center gap-x-2 opacity-70">
          <span className={theme.colors.text.secondary}>
            <span className={theme.colors.text.primary}>{Object.keys(citations).length}</span>{' '}
            referencias
          </span>
          <span className={theme.colors.text.secondary}>
            <span className={theme.colors.text.primary}>{uniqueDocsCount}</span> docs
          </span>
        </div>
      </div>

      {/* Lista de Referencias - Formato estándar discreto (estilo APA/Chicago simplificado) */}
      <div
        className={`text-[9px] ${theme.colors.text.secondary}/60 space-y-0.5 pl-2 border-l ${theme.colors.border.primary}/20`}
      >
        {Object.entries(citations).map(([number, citation]) => (
          <button
            key={number}
            onClick={() => onCitationClick?.(citation)}
            className={`w-full text-left py-0.5 px-1 rounded hover:${theme.colors.bg.secondary}/30 transition-colors group flex items-center gap-x-1.5`}
          >
            <span className={`text-[8px] ${theme.colors.text.primary}/60 font-medium`}>
              [{number}]
            </span>
            <span
              className={`truncate flex-1 ${theme.colors.text.secondary} group-hover:${theme.colors.text.primary}`}
            >
              {getCitationLabel(citation)}
            </span>
            <span className={`text-[8px] ${theme.colors.text.secondary}/50 flex-shrink-0`}>
              p. {citation.page}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};
